import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {AuthApiService} from '../auth/auth-api.service';

export type ConnectAuthType = 'oauth' | 'apiKey' | 'bearer' | 'basic' | 'sftp';

export interface ConnectOAuth2ClientConfig {
  scopes?: string[];
}

export interface ConnectOAuth2Config {
  authorization_url?: string;
  token_url?: string;
  required_scopes?: string[];
  optional_scopes?: string[];
  // Deprecated: kept for backward compatibility with older catalog entries.
  default_scopes?: string[];
  use_pkce?: boolean;
  client?: ConnectOAuth2ClientConfig;
  app_id?: string;
  extra_params?: Record<string, string>;
}

export type ConnectUtilityKind = 'select' | 'multiselect' | 'autocomplete';

export interface ConnectUtilityDescriptor {
  id: string;
  name: string;
  description?: string;
  kind: ConnectUtilityKind;
  operation: string;
  params?: Record<string, unknown>;
  item: {
    idField: string;
    labelField: string;
    metaFields?: string[];
  };
  pagination?: boolean;
  pageSize?: number;
}

export type ConnectProviderDef = {
  palette?: {
    color?: string;
    background?: string;
    border?: string;
    text?: string;
  };
  id: string;
  name: string;
  ref: string;
  description?: string;
  tags?: string[];
  docsUrl?: string;
  logo?: string;
  auth: {supported: ConnectAuthType[]; default: ConnectAuthType};
  api_key?: {in: 'header'; name: string; prefix?: string; help_text?: string};
  oauth2?: ConnectOAuth2Config;
  utilities?: ConnectUtilityDescriptor[];
};

export interface ConnectProviderCatalog {
  version: 'v1';
  providers: ConnectProviderDef[];
}

type ConnectWireAuthType = ConnectAuthType | 'api_key' | 'oauth2';
type ConnectWireProviderDef = Omit<ConnectProviderDef, 'auth'> & {
  auth: {supported: ConnectWireAuthType[]; default: ConnectWireAuthType};
};
type ConnectWireProviderCatalog = Omit<ConnectProviderCatalog, 'providers'> & {
  providers: ConnectWireProviderDef[];
};

const normalizeAuthType = (value: unknown): ConnectAuthType =>
  value === 'api_key' ? 'apiKey' : value === 'oauth2' ? 'oauth' : value as ConnectAuthType;

const serializeAuthType = (value: ConnectAuthType): string =>
  value === 'apiKey' ? 'api_key' : value === 'oauth' ? 'oauth2' : value;

const normalizeCatalog = (catalog: ConnectWireProviderCatalog): ConnectProviderCatalog => ({
  ...catalog,
  providers: (catalog.providers || []).map(provider => ({
    ...provider,
    auth: {
      ...provider.auth,
      supported: (provider.auth?.supported || []).map(normalizeAuthType),
      default: normalizeAuthType(provider.auth?.default),
    },
  })),
});

const serializeCatalog = (catalog: ConnectProviderCatalog): ConnectWireProviderCatalog => ({
  ...catalog,
  providers: (catalog.providers || []).map(provider => ({
    ...provider,
    auth: {
      ...provider.auth,
      supported: (provider.auth?.supported || []).map(serializeAuthType) as ConnectWireAuthType[],
      default: serializeAuthType(provider.auth?.default) as ConnectWireAuthType,
    },
  })),
});

export interface ConnectUtilityItem {
  id: string;
  label: string;
  meta?: Record<string, unknown>;
}

export interface ConnectUtilityItemsResponse {
  items: ConnectUtilityItem[];
  next?: {after: string | null};
}

@Injectable({
  providedIn: 'root',
})
export class ConnectProvidersService {
    private readonly api = inject(AuthApiService);

  fetchCatalog(): Observable<ConnectProviderCatalog> {
    return this.api.get<ConnectWireProviderCatalog>(`/api/v1/providers`).pipe(map(normalizeCatalog));
  }

  updateCatalog(catalog: ConnectProviderCatalog): Observable<ConnectProviderCatalog> {
    return this.api
      .put<ConnectWireProviderCatalog>(`/api/v1/providers`, serializeCatalog(catalog))
      .pipe(map(normalizeCatalog));
  }

  getUtilities(providerId: string): Observable<ConnectUtilityDescriptor[]> {
    return this.api.get<ConnectUtilityDescriptor[]>(`/api/v1/providers/${encodeURIComponent(providerId)}/utilities`);
  }

  getUtilityItems(params: {
    providerId: string;
    utilityId: string;
    connectionId?: string;
    q?: string;
    after?: string;
    limit?: number;
    params?: Record<string, string | number | boolean | null | undefined>;
  }): Observable<ConnectUtilityItemsResponse> {
    const searchParams = new URLSearchParams();
    if (params.connectionId) searchParams.set('connectionId', params.connectionId);
    if (params.q) searchParams.set('q', params.q);
    if (params.after) searchParams.set('after', params.after);
    if (params.limit != null) searchParams.set('limit', String(params.limit));
    if (params.params) {
      Object.entries(params.params).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        searchParams.set(key, String(value));
      });
    }
    const qs = searchParams.toString();
    return this.api.get<ConnectUtilityItemsResponse>(
      `/api/v1/providers/${encodeURIComponent(params.providerId)}/utilities/${encodeURIComponent(params.utilityId)}/list?${qs}`,
    );
  }
}
